import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef,
  EventEmitter, NgZone, OnInit, ViewChild } from '@angular/core';
import { SelectionModel } from "@angular/cdk/collections";
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject, Subscription } from 'rxjs';
import { DocumentData, DocumentsGraphComponent } from 'src/app/components/dashboard-widgets/documents-graph/documents-graph.component';
import { AddOrganizationSubscriptionDialogComponent } from 'src/app/dialogs/add-organization-subscription-dialog/add-organization-subscription-dialog.component';
import { ClientType, Organization, OrganizationSubscription, OrganizationSubscriptionPlan } from 'src/app/models/organization';
import { Paginator } from 'src/app/models/paginator';
import { Plan } from 'src/app/models/plan';
import { User } from 'src/app/models/user';
import { AdminService } from 'src/app/services/admin.service';
import { MessageService } from 'src/app/services/message.service';
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { AnalyzerConfiguration } from 'src/app/models/analyzerConfiguration';
import { EditAnalyzerConfigurationDialogComponent } from 'src/app/dialogs/edit-analyzer-configuration-dialog/edit-analyzer-configuration-dialog.component';
import { TrademarkBulkSynchronization } from 'src/app/models/trademarkBulkSynchronization';
import { OrganizationDeletionLaunch, OrganizationPendingDeletion } from 'src/app/models/organizationDeletionJob';
import { TrademarksBulkSynchronizationErrorsDialogComponent, TrademarksBulkSynchronizationErrorsDialogData } from 'src/app/dialogs/trademarks-bulk-synchronization-errors-dialog/trademarks-bulk-synchronization-errors-dialog.component';
import { Trademark } from 'src/app/models/trademark';
import { Option } from 'src/app/components/select/select.component';
import { synchronizationFilterOptions } from 'src/app/helpers/trademark-synchronization.helper';
import { PageEvent } from '@angular/material/paginator';
import { Territory } from 'src/app/models/territory';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { OrganizationAccessDialogComponent, OrganizationAccessDialogData } from 'src/app/dialogs/organization-access-dialog/organization-access-dialog.component';
import { AclService } from 'src/app/services/acl.service';

@Component({
  selector: 'app-organization-dashboard',
  templateUrl: './organization-dashboard.component.html',
  styleUrls: ['./organization-dashboard.component.scss']
})
export class OrganizationDashboardComponent implements OnInit {

  loading: boolean = false;
  _id?: string;
  organization?: Organization;
  clientTypeOptions: { label: string, value: ClientType }[] = Object.values(ClientType).map((clientType) => ({
    label: `CREATE_ORGANIZATION_DIALOG.CLIENT_TYPE_${clientType.toUpperCase()}`,
    value: clientType
  }));
  documentsStats: {
    documents: number;
    pages: number;
    size: number;
    cost: number;
    types: { _id: string, count: number }[],
    classes: { _id: string, count: number }[]
  } = {
      documents: 0,
      pages: 0,
      size: 0,
      cost: 0,
      types: [],
      classes: []
    };
  documentsGraph: DocumentData[] = [];
  period: "hour" | "day" | "week" | "month" | "year" = "day";
  periods: any[] = [
    {
      selected: false,
      label: "DATES.HOURLY",
      value: "hour"
    },
    {
      selected: true,
      label: "DATES.DAILY",
      value: "day"
    },
    {
      selected: false,
      label: "DATES.WEEKLY",
      value: "week"
    },
    {
      selected: false,
      label: "DATES.MONTHLY",
      value: "month"
    },
    {
      selected: false,
      label: "DATES.YEARLY",
      value: "year"
    }
  ];
  metric: "documents" | "size" | "pages" | "cost" = "documents";
  metrics: any[] = [
    {
      selected: true,
      label: "ADMIN.METRICS.DOCUMENTS",
      value: "documents"
    },
    {
      selected: false,
      label: "ADMIN.METRICS.PAGES",
      value: "pages"
    },
    {
      selected: false,
      label: "ADMIN.METRICS.SIZE",
      value: "size"
    },
    {
      selected: false,
      label: "ADMIN.METRICS.COST",
      value: "cost"
    },
  ]
  @ViewChild("documentsGraph") documentsGraphComponent?: DocumentsGraphComponent;

  users: Paginator<User> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 5,
    totalDocs: 0,
    sort: 'lastName'
  };
  filteredUsers: User[] = [];
  displayedColumns: string[] = ['avatar', 'lastName', 'firstName', 'email', 'profile', 'createdAt', 'actions'];
  usersDataSource: MatTableDataSource<User> = new MatTableDataSource<User>([]);
  tmSelection = new SelectionModel<Trademark>(true, []);
  query?: string;
  usersStats: any[] = [];

  trademarksStats: {
    total: number,
    geographies: { _id: string, count: number }[],
    classes: { _id: string, count: number }[],
    statuses: { _id: string, count: number }[]
  } = {
      total: 0,
      geographies: [],
      classes: [],
      statuses: []
    };

  caseStats: {
    total: number,
    states: { _id: string, count: number }[]
  } = {
      total: 0,
      states: []
    };

  volumeStats: number = 0;

  @ViewChild('pricepiediv') chartElement?: ElementRef<HTMLElement>;
  private chart?: am4charts.PieChart;

  analyzerConfiguration?: AnalyzerConfiguration;
  defaultAnalyzerConfiguration: AnalyzerConfiguration = {
    thresholds: {
      logoDetection: 0.5,
      logoDetectionImage: 0.3,
      imageSimilarity: 0.8,
      textSimilarity: 0.6,
      psSimilarity: 0.8
    },
    defaultValues: {
      countryGuessedFromLang: 0.5,
      countryGuessedFromLangEN: 0.2,
      otherDocumentClass: 0.1
    },
    organizationId: "none"
  };
  isDefaultAnalyzerConfiguration: boolean = false;


  trademarkBulkSynchronizations: Paginator<TrademarkBulkSynchronization> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredTrademarkBulkSynchronizations: TrademarkBulkSynchronization[] = [];
  displayedColumnsTrademarkBulkSynchronizations: string[] = ['sync-createdAt', 'sync-trademarks', 'sync-status', 'sync-report', 'sync-requester', 'sync-errors'];

  trademarks: Paginator<Trademark> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: undefined,
  };

  trademarksFilters: {
    query?: string,
    countryOfDesignation: string[],
    synchronization: string[]
  } = {
      query: undefined,
      countryOfDesignation: [],
      synchronization: []
    }

  readonly synchronizationOptions: Option[] = synchronizationFilterOptions();

  private readonly filtersSubject = new Subject<{
    query?: string,
    countryOfDesignation: string[],
    synchronization: string[]
  }>();
  private trademarksFiltersSubscription?: Subscription;

  trademarksDataSource: MatTableDataSource<Trademark> = new MatTableDataSource<Trademark>([]);
  noTrademarks: boolean = false;

  /** Pending (scheduled, cancellable) deletion for this org, if any. */
  deletionPending?: OrganizationPendingDeletion;

  /**
   * True when we could not determine the pending-deletion state (probe failed).
   * While true, do not show the Delete button (which would 409); show a message
   * instead of silently assuming "no pending deletion".
   */
  deletionStateUnknown = false;

  /** Entering this organisation (organisation access): platform Super Admins and the Owner, outside an access. */
  readonly canEnter: boolean;

  constructor(
    private _message: MessageService,
    private aclService: AclService,
    private adminService: AdminService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private zone: NgZone,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.canEnter = this.aclService.isSuperAdmin() && !this.aclService.isInAccess();
    this.trademarksFiltersSubscription = this.filtersSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(async () => {
        this.resetTrademarksPage();
        await this.retrieveOrganizationTrademarks(false);
      });
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.params);
    this._id = params['id'];
    this.retrieveOrganization(params['id']);
    this.retrievePendingDeletion();
  }


  async retrieveOrganization(id: string) {
    try {
      this._message.emitChange("LOADING", "START");
      this.loading = true;
      this.organization = await this.adminService.retrieveOrganization(id);
      this.retrieveOrganizationUsers();
      this.retrieveOrganizationUsersStats();
      this.retrieveOrganizationDocumentsStats();
      this.retrieveOrganizationDocumentsGraph();
      this.retrieveOrganizationTrademarkStats();
      this.retrieveOrganizationCaseStats();
      this.retrieveOrganizationVolumeStats();
      this.retrieveOrganizationAnalyzerConfiguration();
      this.retrieveTrademarkBulkSynchronizations();
      this.retrieveOrganizationTrademarks(true);
      setTimeout(() => {
        this.makePriceChart();
      }, 200)

      this.loading = false;
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.router.navigate(['errors', '404'])
      }
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  /** Why this organisation cannot be entered right now, as a translation key; undefined when it can. */
  enterBlockedReason(): string | undefined {
    if (this.organization?.isSuspended) {
      return 'ACCESS.BLOCKED_SUSPENDED';
    }
    if (this.deletionPending) {
      return 'ACCESS.BLOCKED_DELETION';
    }
    return undefined;
  }

  enter() {
    if (!this.organization?._id || this.enterBlockedReason()) return;
    const data: OrganizationAccessDialogData = { organization: { id: this.organization._id, name: this.organization.name } };
    this.dialog.open(OrganizationAccessDialogComponent, {
      panelClass: 'dialog-container',
      width: '520px',
      data
    });
  }

  protected async handleDelete() {
    if (!this._id || !this.organization) return;

    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.DELETE_ORG_TITLE',
        text: this.translate.instant(
          'ORGANIZATION.DELETE_ORG_TEXT',
          { name: this.organization.name },
        ),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        },
        confirmation: {
          text: 'ORGANIZATION.DELETE_ORG_CONFIRMATION',
          value: this.organization.name
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (!result || !result.confirmed) return;

    let launch: OrganizationDeletionLaunch;
    try {
      this._message.emitChange('LOADING', 'START');
      this.loading = true;
      launch = await this.adminService.deleteOrganization(this._id);
    } catch {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
      this._message.emitChange('LOADING', 'END');
      this.loading = false;
      return;
    }

    // The deletion is scheduled, not run now: just confirm the schedule and show the banner.
    this.deletionPending = { jobId: launch.jobId, scheduledFor: launch.scheduledFor };
    this._message.emitChange('LOADING', 'END');
    this.loading = false;
    this.toastr.success(this.translate.instant('ORGANIZATION.DELETE_ORG_SCHEDULED'));
  }

  protected async cancelDeletion() {
    if (!this._id || !this.deletionPending) return;

    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.CANCEL_DELETE_TITLE',
        text: this.translate.instant('ORGANIZATION.CANCEL_DELETE_TEXT', { name: this.organization?.name }),
        button: {
          text: 'ACTIONS.CANCEL',
          class: 'danger-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (!result || !result.confirmed) return;

    try {
      await this.adminService.cancelOrganizationDeletion(this._id);
      this.deletionPending = undefined;
      this.toastr.success(this.translate.instant('ORGANIZATION.CANCEL_DELETE_SUCCESS'));
    } catch {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  // Load any pending (cancellable) deletion so the banner shows on refresh.
  protected async retrievePendingDeletion() {
    if (!this._id) return;
    try {
      const pending = await this.adminService.retrievePendingDeletion(this._id);
      this.deletionPending = pending ?? undefined;
      this.deletionStateUnknown = false;
    } catch {
      // Do NOT treat a probe failure as "no pending deletion": that would show a delete button
      this.deletionStateUnknown = true;
    }
  }

  async onClientTypeChange(event: any) {
    const clientType = event?.value?.value as ClientType;
    if (!this.organization?._id || !clientType || clientType === this.organization.clientType) return;
    try {
      this.loading = true;
      this._message.emitChange('LOADING', 'START');
      await this.adminService.updateOrganization(this.organization._id, { clientType });
      this._message.emitChange('LOADING', 'END');
      this.loading = false;
      this.toastr.success(this.translate.instant('ADMIN.ORGANIZATION_CLIENT_TYPE_SUCCESS'));
      if (this._id) {
        this.retrieveOrganization(this._id);
      }
    } catch (err) {
      this.loading = false;
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async retrieveOrganizationUsers() {
    try {
      if (this.organization && this.organization._id) {
        this.users = { ...this.users, ...await this.adminService.retrieveOrganizationUsers(this.organization?._id, this.users.page, this.users.limit, this.users.offset, this.users.sort, this.query) };
        this.filteredUsers = this.users.docs;
        this.usersDataSource = new MatTableDataSource<User>(this.filteredUsers);
      }
    } catch (err) {
      console.log(err)
    }
  }

  async retrieveTrademarkBulkSynchronizations() {
    try {
      if (this.organization && this.organization._id) {
        this.trademarkBulkSynchronizations = { ...this.trademarkBulkSynchronizations, ...await this.adminService.retrieveTrademarkBulkSynchronizations(this.organization?._id, this.trademarkBulkSynchronizations.page, this.trademarkBulkSynchronizations.limit, this.trademarkBulkSynchronizations.offset, this.trademarkBulkSynchronizations.sort) };
        this.filteredTrademarkBulkSynchronizations = this.trademarkBulkSynchronizations.docs;
      }
    } catch (err) {
      console.log(err)
    }
  }

  async handleSyncPage(event: any) {
    this.trademarkBulkSynchronizations.limit = event.pageSize;
    this.trademarkBulkSynchronizations.page = event.pageIndex + 1;
    this.trademarkBulkSynchronizations.offset = event.pageIndex * this.trademarkBulkSynchronizations.limit;

    this.retrieveTrademarkBulkSynchronizations();
  }

  async retrieveOrganizationTrademarks(init: boolean) {
    try {

      this._message.emitChange('LOADING', 'START');
      if (this.organization && this.organization._id) {
        const result = await this.adminService.retrieveTrademarks(
          this.organization._id,
          this.trademarks.page,
          this.trademarks.limit,
          this.trademarks.offset,
          this.trademarks.sort,
          this.trademarksFilters.countryOfDesignation,
          this.trademarksFilters.query,
          this.trademarksFilters.synchronization,
        );
        this.trademarks = { ...this.trademarks, ...result };
        this.trademarksDataSource = new MatTableDataSource<Trademark>(this.trademarks.docs);

        if (init && this.trademarks.totalDocs === 0) {
          this.noTrademarks = true;
        } else if (init && this.trademarks.totalDocs > 0) {
          this.noTrademarks = false;
        }
      }
      this._message.emitChange('LOADING', 'END');
    } catch (err) {
      console.error(err);
      this._message.emitChange('LOADING', 'END');
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async handleTrademarkPage(event: PageEvent) {
    this.trademarks.limit = event.pageSize;
    this.trademarks.page = event.pageIndex + 1;
    this.trademarks.offset = event.pageIndex * this.trademarks.limit;
    this.retrieveOrganizationTrademarks(false);
  }

  onTrademarkSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.trademarks.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      this.retrieveOrganizationTrademarks(false);
    }
  }

  onTrademarkCountryOfDesignationChange(event: any) {
    this.trademarksFilters.countryOfDesignation = event.value.map((t: Territory) => t.id);
    this.filtersSubject.next({ ...this.trademarksFilters });
  }

  onTrademarkSynchronizationChange(event: any) {
    this.trademarksFilters.synchronization = event.value;
    this.filtersSubject.next({ ...this.trademarksFilters });
  }

  onQueryChange(query: any) {
    this.filtersSubject.next({ ...this.trademarksFilters });
  }

  resetTrademarksPage() {
    this.trademarks.limit = 10;
    this.trademarks.page = 1;
    this.trademarks.offset = 0;
  }

  async bulkSynchronize() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.BULK_SYNCHRONIZE_TITLE',
        text: this.translate.instant('ORGANIZATION.BULK_SYNCHRONIZE_TEXT', { number: this.trademarks.totalDocs, organization: this.organization?.name }),
        button: {
          text: 'ACTIONS.SYNCHRONIZE',
          class: 'main-button'
        },
        confirmation: {
          text: 'ORGANIZATION.BULK_SYNCHRONIZE_CONFIRMATION',
          value: "Oui, j'ai bien lu TOUT le texte"
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.organization && this.organization._id && this.trademarks.totalDocs > 0) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        this.adminService.bulkSynchronizeTrademarks(this.organization?._id, this.trademarksFilters.countryOfDesignation, this.trademarksFilters.query, this.trademarksFilters.synchronization);
        this.trademarkBulkSynchronizations.limit = 10;
        this.trademarkBulkSynchronizations.page = 1;
        this.trademarkBulkSynchronizations.offset = 0;
        await this.retrieveTrademarkBulkSynchronizations()
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('ORGANIZATION.BULK_SYNCHRONIZE_SUCCESS'));
      } catch (err) {
        console.error(err);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async deleteTrademarks() {
    const length = this.tmSelection.selected.length;
    if (!this.organization?._id || length === 0) return;

    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'ORGANIZATION.DELETE_TRADEMARKS_TITLE',
        text: this.translate.instant(
          'ORGANIZATION.DELETE_TRADEMARKS_TEXT',
          { length },
        ),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button',
        },
      },
    };
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: {
      confirmed: boolean
    } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      try {
        this.loading = true;
        this._message.emitChange('LOADING', 'START');
        await this.adminService.deleteTrademarks(
          this.organization._id,
          this.tmSelection.selected.map((tm) => tm._id!),
        );
        this.toastr.success(this.translate.instant(
          'ORGANIZATION.TRADEMARKS_DELETED',
          { length },
        ));
        this.tmSelection.clear();
        await this.retrieveOrganizationTrademarks(false);
      } catch (err) {
        this.toastr.error('ERRORS.GENERIC');
      } finally {
        this.loading = false;
        this._message.emitChange('LOADING', 'END');
      }
    }
  }

  async retrieveOrganizationUsersStats() {
    try {
      if (this.organization && this.organization._id) {
        this.usersStats = await this.adminService.retrieveOrganizatioUsersStats(this.organization._id);
      }

    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationDocumentsStats() {
    try {
      if (this.organization && this.organization._id) {
        this.documentsStats = await this.adminService.retrieveOrganizationDocumentsStats(this.organization._id);
        console.log(this.documentsStats)
      }

    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationDocumentsGraph() {
    try {

      if (this.organization && this.organization._id) {
        this.documentsGraph = await this.adminService.retrieveOrganizationDocumentsGraph(this.organization._id, undefined, undefined, this.period);
      }
    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationTrademarkStats() {
    try {
      if (this.organization && this.organization._id) {
        this.trademarksStats = await this.adminService.retrieveOrganizationTrademarksStats(this.organization._id);
      }

    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationCaseStats() {
    try {
      if (this.organization && this.organization._id) {
        this.caseStats = await this.adminService.retrieveOrganizationCasesStats(this.organization._id);
      }

    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationVolumeStats() {
    try {
      if (this.organization && this.organization._id) {
        const volumeStats = await this.adminService.retrieveOrganizationVolumeStats(this.organization._id);
        console.log(volumeStats)
        this.volumeStats = volumeStats.length == 1 ? volumeStats[0].count / 1024 / 1024 / 1024 : 0;
      }

    } catch (err) {
      console.log(err)
    }
  }

  async retrieveOrganizationAnalyzerConfiguration() {
    try {
      if (this.organization && this.organization._id) {
        const configuration = await this.adminService.retrieveOrganizationAnalyzerConfiguration(this.organization._id);
        if (!configuration) {
          this.analyzerConfiguration = this.defaultAnalyzerConfiguration;
          this.isDefaultAnalyzerConfiguration = true;
        } else {
          this.analyzerConfiguration = configuration;
          this.isDefaultAnalyzerConfiguration = false;
        }
      }

    } catch (err) {
      console.log(err)
    }
  }

  async editAnalyzerConfiguration() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: { analyzerConfiguration: this.analyzerConfiguration, organization: this.organization }
    }
    const dialog = this.dialog.open(EditAnalyzerConfigurationDialogComponent, config);
    const result: {
      edited: boolean,
      analyzerConfiguration?: AnalyzerConfiguration
    } = await firstValueFrom(dialog.afterClosed());

    if (result && result.edited && result.analyzerConfiguration && this.organization && this.organization._id) {
      try {
        this._message.emitChange("LOADING", "START");
        result.analyzerConfiguration.organizationId = this.organization._id;
        await this.adminService.updateOrganizationAnalyzerConfiguration(this.organization?._id, result.analyzerConfiguration);
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('ORGANIZATION.ANALYZER_CONFIGURATION.EDIT_CONFIGURATION_SUCCESS', { name: this.organization.name }));
        this.retrieveOrganizationAnalyzerConfiguration();
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async handleUsersPage(event: any) {
    this.users.limit = event.pageSize;
    this.users.page = event.pageIndex + 1;
    this.users.offset = event.pageIndex * this.users.limit;

    this.retrieveOrganizationUsers();
  }

  onSortUsersChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.users.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      this.retrieveOrganizationUsers();
    }
  }

  async onPeriodChange(event: any) {
    this.period = this.periods[event].value;
    await this.retrieveOrganizationDocumentsGraph();
  }

  async onMetricChange(event: any) {
    this.metric = this.metrics[event].value;
    // if (this.documentsGraphComponent) {
    //   await this.documentsGraphComponent.makeChart();
    // }
  }


  formatDate(date: Moment | Date | string) {
    return moment(date).format('ll');
  }

  formatDateHour(date: Moment) {
    return moment(date).format('lll');
  }

  formatSize(size: number) {
    if (size < 1024) {
      return {
        value: size,
        unit: 'o'
      }
    } else if (size < 1024 * 1024) {
      return {
        value: size / 1024,
        unit: 'Ko'
      }
    } else if (size < 1024 * 1024 * 1024) {
      return {
        value: size / (1024 * 1024),
        unit: 'Mo'
      }
    } else if (size < 1024 * 1024 * 1024 * 1024) {
      return {
        value: size / (1024 * 1024 * 1024),
        unit: 'Go'
      }
    }
    return {
      value: size,
      unit: 'o'
    }
  }

  getSyncProgress(sync: TrademarkBulkSynchronization) {
    if (sync.trademarks > 0) {
      return Math.round(100 * sync.report.done / sync.trademarks)
    }
    return undefined;
  }

  getSyncScore(sync: TrademarkBulkSynchronization) {
    if (sync.report.done > 0) {
      return Math.round(100 * sync.report.success / sync.report.done)
    }
    return undefined;
  }

  getSyncTooltip(sync: TrademarkBulkSynchronization, type: string) {
    return `${sync.report[type as keyof {
      done: number,
      success: number,
      error: number
    }]} ${this.translate.instant('SYNC.TRADEMARKS_IN_' + type.toUpperCase()).toLowerCase()}`
  }

  showSyncErrors(sync: TrademarkBulkSynchronization) {
    const organizationId = sync.organizationId ?? this.organization?._id;
    const data: TrademarksBulkSynchronizationErrorsDialogData = {
      errors: sync.errors,
      total: sync.report?.error,
      organization: organizationId ? { id: organizationId, name: this.organization?.name ?? '' } : undefined,
      canEnter: this.canEnter && !this.organization?.platform,
      enterBlockedReason: this.enterBlockedReason()
    };
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '1200px',
      maxHeight: '600px',
      autoFocus: false,
      data
    }
    this.dialog.open(TrademarksBulkSynchronizationErrorsDialogComponent, config);
  }

  add(a: number, b: number) {
    return a / 1 + b / 1
  }

  totalVolume() {
    if (this.organization && this.organization.subscription && this.organization.subscription.plan) {
      return this.add(this.organization.subscription.plan.maxVolume, this.organization.subscription.additionalVolume || 0)
    }
    return 0;
  }

  totalTrademarks() {
    if (this.organization && this.organization.subscription && this.organization.subscription.plan) {
      return this.add(this.organization.subscription.plan.maxTrademarks || 0, this.organization.subscription.additionalTrademarks || 0)
    }
    return 0;
  }

  totalCases() {
    if (this.organization && this.organization.subscription && this.organization.subscription.plan) {
      return this.add(this.organization.subscription.plan.maxCases || 0, this.organization.subscription.additionalCases || 0)
    }
    return 0;
  }

  formatCost(cost: number) {
    if (!cost)
      return 0;
    if (cost < 1) {
      return parseFloat(cost.toFixed(3))
    }
    return parseFloat(cost.toFixed(2))
  }

  async addSubscription() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {}
    }
    const dialog = this.dialog.open(AddOrganizationSubscriptionDialogComponent, config);
    const result: {
      created: boolean,
      subscription?: OrganizationSubscriptionPlan
    } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.subscription && result.subscription.plan && result.subscription.plan._id && this.organization && this.organization._id) {
      try {
        this._message.emitChange("LOADING", "START");
        const subscription = {
          plan: result.subscription.plan?._id,
          additionalVolume: result.subscription.additionalVolume,
          additionalTrademarks: result.subscription.additionalTrademarks,
          additionalCases: result.subscription.additionalCases,
          additionalCrawlerUrl: result.subscription.additionalCrawlerUrl
        }
        await this.adminService.setOrganizationSubscription(this.organization?._id, subscription);
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('ADMIN.ORGANIZATION_SUBSCRIPTION_SUCCESS', { name: this.organization.name, plan: result.subscription.plan?.label }));
        if (this._id)
          this.retrieveOrganization(this._id);
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  async editSubscription() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: { subscription: this.organization?.subscription }
    }
    const dialog = this.dialog.open(AddOrganizationSubscriptionDialogComponent, config);
    const result: {
      created: boolean,
      subscription?: OrganizationSubscriptionPlan
    } = await firstValueFrom(dialog.afterClosed());
    if (result && result.created && result.subscription && result.subscription.plan && result.subscription.plan._id && this.organization && this.organization._id) {
      try {
        this._message.emitChange("LOADING", "START");
        const subscription = {
          plan: result.subscription.plan?._id,
          additionalVolume: result.subscription.additionalVolume,
          additionalTrademarks: result.subscription.additionalTrademarks,
          additionalCases: result.subscription.additionalCases,
          additionalCrawlerUrl: result.subscription.additionalCrawlerUrl
        }
        await this.adminService.setOrganizationSubscription(this.organization?._id, subscription);
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant('ADMIN.ORGANIZATION_SUBSCRIPTION_SUCCESS', { name: this.organization.name, plan: result.subscription.plan?.label }));
        if (this._id)
          this.retrieveOrganization(this._id);
      } catch (err) {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }


  makePriceChart() {
    const self = this;
    this.zone.runOutsideAngular(() => {
      if (this.chartElement && this.organization && this.organization.subscription && this.organization.subscription.plan) {
        am4core.useTheme(am4themes_animated.default);

        var chart = am4core.create(this.chartElement.nativeElement, am4charts.PieChart);
        chart.radius = am4core.percent(65);
        chart.logo.setVisibility(false);

        // A part that costs nothing (the base plan of a free trial) is left out: its label would sit on
        // top of its neighbour's.
        chart.data = [
          {
            category: this.translate.instant("ORGANIZATION.BASE_PLAN"),
            count: this.organization.subscription.plan.annualPrice,
            color: am4core.color("#8847E3")
          },
          {
            category: this.translate.instant("ORGANIZATION.ADDITIONAL_VOLUME"),
            count: this.additionalVolumePrice(),
            color: am4core.color("#6c38b5")
          },
          {
            category: this.translate.instant("ORGANIZATION.ADDITIONAL_TRADEMARKS"),
            count: this.additionalTrademarksPrice(),
            color: am4core.color("#512a88")
          },
          {
            category: this.translate.instant("ORGANIZATION.ADDITIONAL_CASES"),
            count: this.additionalCasesPrice(),
            color: am4core.color("#361c5a")
          }
        ].filter((part) => !!part.count && part.count > 0);

        var pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = "count";
        pieSeries.dataFields.category = "category";
        pieSeries.slices.template.stroke = am4core.color("#fff");
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.propertyFields.fill = "color";
        pieSeries.labels.template.wrap = true;
        pieSeries.labels.template.maxWidth = 150;
        // Labels in two columns beside the pie, linked to their part: two small neighbouring parts
        // no longer put their labels on top of each other.
        pieSeries.alignLabels = true;
        pieSeries.labels.template.fontSize = 10;

        // This creates initial animation
        pieSeries.hiddenState.properties.opacity = 1;
        pieSeries.hiddenState.properties.endAngle = -90;
        pieSeries.hiddenState.properties.startAngle = -90;

        chart.hiddenState.properties.radius = am4core.percent(0);


        this.chart = chart;
        this.chart.events.on('ready', () => {
          this.changeDetector.detectChanges()
        })
      }
    })
  }

  additionalVolumePrice() {
    if (this.organization && this.organization.subscription && this.organization.subscription.additionalVolume) {
      return this.organization.subscription.additionalVolume * 200
    }
    return undefined
  }

  additionalTrademarksPrice() {
    if (this.organization && this.organization.subscription && this.organization.subscription.additionalTrademarks) {
      return Math.ceil(this.organization.subscription.additionalTrademarks / 5000) * 2500
    }
    return undefined
  }

  additionalCasesPrice() {
    if (this.organization && this.organization.subscription && this.organization.subscription.additionalCases) {
      if (this.organization.subscription.additionalCases >= 1 && this.organization.subscription.additionalCases <= 2) {
        return 675 * this.organization.subscription.additionalCases
      } else if (this.organization.subscription.additionalCases >= 3 && this.organization.subscription.additionalCases <= 7) {
        return 600 * this.organization.subscription.additionalCases
      } else if (this.organization.subscription.additionalCases >= 8 && this.organization.subscription.additionalCases <= 17) {
        return 525 * this.organization.subscription.additionalCases
      } else if (this.organization.subscription.additionalCases >= 18 && this.organization.subscription.additionalCases <= 27) {
        return 450 * this.organization.subscription.additionalCases
      } else if (this.organization.subscription.additionalCases >= 28) {
        return 375 * this.organization.subscription.additionalCases
      }
    }
    return undefined
  }

  subscriptionPrice() {
    if (this.organization && this.organization.subscription && this.organization.subscription.plan !== undefined) {
      return this.organization.subscription.plan?.annualPrice + (this.additionalVolumePrice() || 0) + (this.additionalTrademarksPrice() || 0) + (this.additionalCasesPrice() || 0)
    }
    return undefined;
  }

  round(number: number, decimal: number = 0) {
    return number.toFixed(decimal)
  }
}
